'use client';
import { setCookie } from 'cookies-next';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useApp } from '@/forApp/states/appState/appState';
import { useUser } from '@/forApp/states/userState/userState';
import fetcher from '@/forApp/utils/fetcher/fetcher';
import Button from '@/shared/Buttons/Button';
import Checkbox from '@/shared/Checkboxs/Checkbox';
import Input from '@/shared/Input/input';
import styles from './styles.module.css';
import { ROUTES } from '@/forApp/utils/routes';

interface AgentRegisterFormData {
  user_name: string;
  user_surname: string;
  user_patronymic?: string;
  user_phone: string;
  user_email: string;
  user_password: string;
  user_password_confirm: string;
  device_name: string;
}

const AgentRegister: React.FC = () => {
  const { delModal, addNotification } = useApp();
  const { getUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const router = useRouter();

  // Generate a timestamp-based device identifier
  const generateDeviceId = () => {
    return Date.now().toString();
  };

  const deviceId = generateDeviceId();

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<AgentRegisterFormData>({
    mode: 'onChange',
    defaultValues: {
      device_name: deviceId,
    },
  });

  const password = watch('user_password');

  const onSubmit = async (data: AgentRegisterFormData) => {
    if (data.user_password !== data.user_password_confirm) {
      addNotification({
        type: 'error',
        text: 'Пароли не совпадают',
      });
      return;
    }

    if (!policyAgreed) {
      addNotification({
        type: 'error',
        text: 'Необходимо согласиться с политикой конфиденциальности',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetcher<{ token: string }>(
        'POST',
        '/register/agent',
        {
          body: JSON.stringify({
            ...data,
          }),
        },
      );

      if (response.data.token) {
        // Store the device_name in a cookie for later use
        setCookie('device_name', data.device_name, {
          maxAge: 60 * 60 * 24 * 30,
        }); // 30 days
        setCookie('cg-token', response.data.token);

        addNotification({
          type: 'success',
          text: 'Вы успешно зарегистрировались',
        });
        getUser();
        router.push('/profile');
        delModal();
      }
    } catch {
      addNotification({
        type: 'error',
        text: 'Произошла ошибка при регистрации',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Input
          name="user_name"
          control={control}
          placeholder="Имя"
          rules={{ required: 'Имя обязательно' }}
          autocomplete="name"
        />
        <Input
          name="user_surname"
          control={control}
          placeholder="Фамилия"
          rules={{ required: 'Фамилия обязательна' }}
          autocomplete="family-name"
        />
        <Input
          name="user_patronymic"
          control={control}
          placeholder="Отчество"
          autocomplete="given-name"
        />
        <Input
          name="user_phone"
          control={control}
          placeholder="Телефон"
          rules={{
            required: 'Телефон обязателен',
            validate: (value: string) => {
              // Remove all non-digit characters
              const digitsOnly = value.replace(/\D/g, '');
              // Check if it's a valid Russian mobile number (starts with 7 and has 11 digits)
              return (
                (digitsOnly.length === 11 && digitsOnly.startsWith('7')) ||
                'Введите корректный номер мобильного телефона'
              );
            },
          }}
          mask="+7 (___) ___-__-__"
          autocomplete="phone"
        />
        <Input
          name="user_email"
          control={control}
          placeholder="E-mail"
          rules={{
            required: 'Email обязателен',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Некорректный email',
            },
          }}
          autocomplete="email"
        />
        <Input
          name="user_password"
          control={control}
          placeholder="Пароль"
          type="password"
          rules={{ required: 'Пароль обязателен' }}
          autocomplete="new-password"
        />
        <Input
          name="user_password_confirm"
          control={control}
          placeholder="Повторите пароль"
          type="password"
          rules={{
            required: 'Подтверждение пароля обязательно',
            validate: (value: string) =>
              value === password || 'Пароли не совпадают',
          }}
          autocomplete="new-password"
        />

        <div className={styles.modalFooter}>
          <Checkbox
            isChecked={policyAgreed}
            onChange={() => setPolicyAgreed(!policyAgreed)}
          />
          <span>
            Я согласен(а) с условиями{' '}
            <Link href={ROUTES.POLICY} target="_blank">
              Политики конфиденциальности
            </Link>{' '}
            и даю согласие на обработку персональных данных.{' '}
            <span style={{ color: 'red' }}>*</span>
          </span>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            type="submit"
            color={'dark'}
            disabled={!isValid || !policyAgreed || isLoading}
            isLoading={isLoading}
            text={'Зарегистрироваться'}
            size={'md'}
          />
        </div>
      </form>
    </div>
  );
};

export default AgentRegister;
